/**
 * Pure helpers for the journal ledger screens (tracker item 31): /ledger,
 * /ledger/:id and /ledger/new.
 *
 * Money is handled in integer cents (utils/cents) so the manual entry balance
 * guard never sums floats. It is display arithmetic only: the server validates
 * and posts every entry.
 */

import * as types from '../api/types';
import { centsToDecimal, decimalToCents, parseAmountCents, sumDecimals } from '../utils/cents';
import { invoiceDate, invoiceMoney } from './invoiceUi';

export const LEDGER_PATH = '/ledger';
export const LEDGER_NEW_PATH = '/ledger/new';
export const LEDGER_PAGE_SIZE = 20;
/** Backend limit on a reversal memo (GLBusinessLayer). */
export const REVERSAL_MEMO_MAX_LENGTH = 500;

export const ledgerEntryPath = (id: string) => `${LEDGER_PATH}/${encodeURIComponent(id)}`;

/** /ledger with the given filters in the query string (blank values left out). */
export function ledgerListPath(filters: types.JournalEntryListFilters = {}): string {
  const params = new URLSearchParams();
  (['startDate', 'endDate', 'sourceType', 'accountId'] as const).forEach((key) => {
    const value = filters[key];
    if (value) params.set(key, value);
  });
  const query = params.toString();
  return query ? `${LEDGER_PATH}?${query}` : LEDGER_PATH;
}

const SOURCE_TYPE_LABELS: Record<types.JournalSourceType, string> = {
  INVOICE: 'Invoice',
  PAYMENT: 'Payment',
  ADJUSTMENT: 'Adjustment',
  FINANCE_CHARGE: 'Finance charge',
};

export function sourceTypeLabel(sourceType: string): string {
  return SOURCE_TYPE_LABELS[sourceType as types.JournalSourceType] || sourceType;
}

export function isJournalSourceType(value: string | null | undefined): value is types.JournalSourceType {
  return !!value && (types.JOURNAL_SOURCE_TYPES as readonly string[]).includes(value);
}

/** A sourceType query value, or '' when missing or unknown (the server would 400 on it). */
export function sourceTypeParam(value: string | null | undefined): types.JournalSourceType | '' {
  return isJournalSourceType(value) ? value : '';
}

export const ledgerMoney = (cents: number): string => invoiceMoney(centsToDecimal(cents));
export const ledgerDate = (value: string | null | undefined): string => invoiceDate(value);

/** Display for one line amount: blank for a zero side. */
export function lineAmount(value: string): string {
  const cents = decimalToCents(value);
  if (cents === null) return value;
  return cents === 0 ? '' : ledgerMoney(cents);
}

/** Entry total = sum of its debits (equal to its credits for a posted entry). */
export function entryTotalCents(entry: Pick<types.JournalEntry, 'lines'>): number {
  return sumDecimals(entry.lines.map((line) => line.debit));
}

export function createdByLabel(entry: Pick<types.JournalEntry, 'createdByEmail'>): string {
  return entry.createdByEmail || 'Unknown user';
}

export interface ReversalBadge {
  label: string;
  className: string;
  title: string;
}

/** "Reversed" / "Reversal" badges for the list and detail header. */
export function reversalBadges(entry: Pick<types.JournalEntry, 'reversedByEntryId' | 'reversesEntryId'>): ReversalBadge[] {
  const badges: ReversalBadge[] = [];
  if (entry.reversesEntryId) {
    badges.push({ label: 'Reversal', className: 'bg-purple-50 text-purple-700 ring-purple-600/20', title: 'This entry reverses another entry' });
  }
  if (entry.reversedByEntryId) {
    badges.push({ label: 'Reversed', className: 'bg-amber-50 text-amber-800 ring-amber-600/20', title: 'A later entry reverses this one' });
  }
  return badges;
}

// ============ Inventory postings (tracker item 30) ============

/**
 * Memo prefixes the backend writes on inventory receipt / count adjustment
 * entries (InventoryBusinessLayer.receiptMemo / adjustmentMemo).
 */
export const INVENTORY_ENTRY_MEMO_PREFIXES = ['Stock received: ', 'Count adjustment: '] as const;

/** Shown instead of Reverse for an inventory entry, and for a 400 INVENTORY_ENTRY_NOT_REVERSIBLE. */
export const INVENTORY_ENTRY_NOT_REVERSIBLE_MESSAGE =
  'This entry was posted by an inventory receipt or count adjustment. Reversing it would change GL 1300 without changing ' +
  'the stock, so correct it with another count adjustment on the Inventory page.';

/**
 * Heuristic: true for an entry that looks like an inventory posting. The entry
 * response has no inventory flag, so this reads what the backend writes: an
 * ADJUSTMENT that is not a reversal, with the inventory memo prefix and an item
 * id (not a manual-… id from the ledger form) as sourceId. A manual entry
 * posted through the API with the same memo would also match; the server
 * stays the authority (400 INVENTORY_ENTRY_NOT_REVERSIBLE).
 */
export function isLikelyInventoryEntry(
  entry: Pick<types.JournalEntry, 'sourceType' | 'sourceId' | 'reversesEntryId' | 'memo'>
): boolean {
  return (
    entry.sourceType === 'ADJUSTMENT' &&
    !entry.reversesEntryId &&
    !entry.sourceId.startsWith('manual-') &&
    INVENTORY_ENTRY_MEMO_PREFIXES.some((prefix) => (entry.memo ?? '').startsWith(prefix))
  );
}

export interface SourceLink {
  to: string;
  label: string;
}

/**
 * Route to the document that posted the entry, when there is one: invoices
 * (INVOICE and FINANCE_CHARGE post with the invoice id) and payments. A
 * reversal links to the entry it reverses via reversesEntryId. An
 * inventory receipt / count adjustment links to the item's stock history.
 * Other manual ADJUSTMENT entries carry a free-text sourceId and get no link.
 */
export function sourceDocumentLink(
  entry: Pick<types.JournalEntry, 'sourceType' | 'sourceId' | 'reversesEntryId' | 'memo'>
): SourceLink | null {
  if (isLikelyInventoryEntry(entry)) {
    // Inventory entries carry the item id as sourceId: open its stock history.
    return { to: `/inventory?itemId=${encodeURIComponent(entry.sourceId)}`, label: 'View stock history' };
  }
  switch (entry.sourceType) {
    case 'INVOICE':
      return { to: `/invoices/${encodeURIComponent(entry.sourceId)}`, label: 'View invoice' };
    case 'FINANCE_CHARGE':
      return { to: `/invoices/${encodeURIComponent(entry.sourceId)}`, label: 'View finance-charge invoice' };
    case 'PAYMENT':
      return { to: `/payments/${encodeURIComponent(entry.sourceId)}`, label: 'View payment' };
    default:
      return entry.reversesEntryId ? { to: ledgerEntryPath(entry.reversesEntryId), label: 'View reversed entry' } : null;
  }
}

/**
 * Why the entry cannot be reversed from the ledger, or null when it can.
 * Mirrors the backend's POST /gl/journal-entries/:id/reverse rules: only
 * ADJUSTMENT entries that are not themselves a reversal, have not been
 * reversed yet and are not inventory postings (isLikelyInventoryEntry). The
 * server re-checks (409 ALREADY_REVERSED on a race, 400
 * INVENTORY_ENTRY_NOT_REVERSIBLE for an inventory entry the heuristic missed).
 */
export function reversalBlockReason(
  entry: Pick<types.JournalEntry, 'sourceType' | 'reversedByEntryId' | 'reversesEntryId'> &
    Partial<Pick<types.JournalEntry, 'sourceId' | 'memo'>>
): string | null {
  switch (entry.sourceType) {
    case 'INVOICE':
      return 'This entry was posted by an invoice. Cancel the invoice to reverse it, so the invoice and its accounting stay in step.';
    case 'FINANCE_CHARGE':
      return 'This entry was posted by a finance-charge invoice. Cancel that invoice to reverse it.';
    case 'PAYMENT':
      return 'This entry was posted by a payment. Void the payment to reverse it, so the payment and its accounting stay in step.';
    default:
      break;
  }
  if (entry.reversesEntryId) {
    return 'This entry is itself a reversal and cannot be reversed. Post a new manual entry instead.';
  }
  if (entry.reversedByEntryId) {
    return 'This entry has already been reversed.';
  }
  if (isLikelyInventoryEntry({ ...entry, sourceId: entry.sourceId ?? '' })) {
    return INVENTORY_ENTRY_NOT_REVERSIBLE_MESSAGE;
  }
  return null;
}

// ============ Account picker ============

export function accountLabel(account: Pick<types.GLAccount, 'accountNumber' | 'accountName'>): string {
  return `${account.accountNumber} · ${account.accountName}`;
}

/**
 * Postable accounts matching `query` (account number prefix or name
 * substring, case-insensitive), sorted by account number. The account in
 * `keepId` (the line's current pick) is always kept so the select never
 * silently drops its value while filtering.
 */
export function filterPostableAccounts(accounts: types.GLAccount[], query: string, keepId = ''): types.GLAccount[] {
  const needle = query.trim().toLowerCase();
  return accounts
    .filter((account) => account.isPostable)
    .filter((account) =>
      !needle ||
      account.id === keepId ||
      account.accountNumber.toLowerCase().startsWith(needle) ||
      account.accountName.toLowerCase().includes(needle) ||
      accountLabel(account).toLowerCase().includes(needle)
    )
    .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber, undefined, { numeric: true }));
}

// ============ Manual entry form ============

export interface DraftLine {
  /** Client-only React key */
  key: string;
  glAccountId: string;
  /** Account search text for this line's picker */
  search: string;
  debit: string;
  credit: string;
  /** AR subledger tag; only sent for a receivable (1200) line, '' = untagged */
  customerId: string;
  /** Customer search text for this line's picker */
  customerSearch: string;
}

export interface ManualEntryForm {
  entryDate: string;
  memo: string;
  lines: DraftLine[];
}

export const MIN_LINES = 2;

export function emptyDraftLine(key: string): DraftLine {
  return { key, glAccountId: '', search: '', debit: '', credit: '', customerId: '', customerSearch: '' };
}

/** Local calendar date as YYYY-MM-DD (the user's "today", not UTC's). */
export function localToday(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function emptyManualEntryForm(today: string, makeKey: () => string): ManualEntryForm {
  return { entryDate: today, memo: '', lines: [emptyDraftLine(makeKey()), emptyDraftLine(makeKey())] };
}

/**
 * Setting one side of a line clears the other, so each line is a debit or a
 * credit (the backend rejects a line with both).
 */
export function setLineAmount(line: DraftLine, side: 'debit' | 'credit', value: string): DraftLine {
  const other = side === 'debit' ? 'credit' : 'debit';
  return { ...line, [side]: value, ...(value.trim() ? { [other]: '' } : {}) };
}

/** Cents on each side of a line; invalid or blank input counts as 0. */
function lineCents(line: DraftLine): { debit: number; credit: number } {
  return { debit: parseAmountCents(line.debit) ?? 0, credit: parseAmountCents(line.credit) ?? 0 };
}

/**
 * Error for one line's amounts, shown next to the line as soon as it applies.
 * Missing account / missing amount are reported by submitBlocker instead, so
 * a fresh line is not covered in red.
 */
export function lineAmountError(line: DraftLine): string | null {
  const debitText = line.debit.trim();
  const creditText = line.credit.trim();
  if (debitText && parseAmountCents(debitText) === null) return 'Debit must be an amount like 125.00 (at most 2 decimals, not negative).';
  if (creditText && parseAmountCents(creditText) === null) return 'Credit must be an amount like 125.00 (at most 2 decimals, not negative).';
  const { debit, credit } = lineCents(line);
  if (debit > 0 && credit > 0) return 'Enter a debit or a credit, not both.';
  if ((debitText || creditText) && debit === 0 && credit === 0) return 'The amount must be greater than zero.';
  return null;
}

export interface DraftTotals {
  debitCents: number;
  creditCents: number;
  /** debit - credit; 0 when balanced */
  differenceCents: number;
}

export function draftTotals(lines: DraftLine[]): DraftTotals {
  let debitCents = 0;
  let creditCents = 0;
  lines.forEach((line) => {
    const cents = lineCents(line);
    debitCents += cents.debit;
    creditCents += cents.credit;
  });
  return { debitCents, creditCents, differenceCents: debitCents - creditCents };
}

/** "Out of balance by $X (debits exceed credits)", or null when balanced. */
export function outOfBalanceMessage(totals: DraftTotals): string | null {
  if (totals.differenceCents === 0) return null;
  const side = totals.differenceCents > 0 ? 'debits exceed credits' : 'credits exceed debits';
  return `Out of balance by ${ledgerMoney(Math.abs(totals.differenceCents))} (${side}).`;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The first reason the form cannot be submitted, or null when it can. Mirrors
 * the backend posting rules: a date, at least 2 lines, an account and exactly
 * one positive side on every line, total > 0 and debits = credits.
 */
export function submitBlocker(form: ManualEntryForm): string | null {
  if (!DATE_ONLY.test(form.entryDate)) return 'Enter an entry date.';
  if (form.lines.length < MIN_LINES) return `An entry needs at least ${MIN_LINES} lines.`;
  if (form.lines.some((line) => lineAmountError(line))) return 'Fix the amounts marked on the lines.';
  if (form.lines.some((line) => !line.glAccountId)) return 'Choose an account on every line.';
  if (form.lines.some((line) => !line.debit.trim() && !line.credit.trim())) return 'Enter a debit or a credit on every line.';
  const totals = draftTotals(form.lines);
  if (totals.debitCents === 0) return 'The entry total must be greater than zero.';
  return outOfBalanceMessage(totals);
}

/**
 * A sourceId for a manual entry that can never equal another entry's id (a
 * manual ADJUSTMENT whose sourceId is an entry id is treated by the backend as
 * that entry's reversal). Does not rely on crypto.randomUUID (absent in jsdom).
 */
export function manualSourceId(now: Date = new Date(), random: () => number = Math.random): string {
  const suffix = Array.from({ length: 2 }, () => random().toString(36).slice(2, 10).padEnd(8, '0')).join('');
  return `manual-${now.getTime().toString(36)}-${suffix}`;
}

/**
 * POST /gl/journal-entries body for a submittable form. Manual entries post
 * as ADJUSTMENT (there is no MANUAL source type). Amounts are 2-place decimal
 * strings and the unused side is "0"; a blank memo is left out.
 */
export function buildCreateJournalEntryRequest(form: ManualEntryForm, sourceId: string): types.CreateJournalEntryRequest {
  const memo = form.memo.trim();
  return {
    entryDate: form.entryDate,
    ...(memo ? { memo } : {}),
    sourceType: 'ADJUSTMENT',
    sourceId,
    lines: form.lines.map((line) => {
      const cents = lineCents(line);
      return {
        glAccountId: line.glAccountId,
        debit: cents.debit > 0 ? centsToDecimal(cents.debit) : '0',
        credit: cents.credit > 0 ? centsToDecimal(cents.credit) : '0',
        ...(line.customerId ? { customerId: line.customerId } : {}),
      };
    }),
  };
}

/**
 * Customers matching `search` (name, email or phone, case-insensitive), by name.
 * The line's current pick is always kept so the select never loses its value.
 */
export function filterCustomers(customers: types.Customer[], search: string, keepId = ''): types.Customer[] {
  const needle = search.trim().toLowerCase();
  return customers
    .filter((customer) => !needle || customer.id === keepId ||
      customer.name.toLowerCase().includes(needle) ||
      (customer.email ?? '').toLowerCase().includes(needle) ||
      (customer.phone ?? '').toLowerCase().includes(needle))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** True when a line posts to Accounts Receivable, whose lines carry the customer subledger tag. */
export function isReceivableAccount(account: Pick<types.GLAccount, 'accountNumber'> | undefined): boolean {
  return account?.accountNumber === '1200';
}
